import React, { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Settings, Info } from 'lucide-react';
import { EstimateItem, useEstimate } from '@/contexts/EstimateContext';
import EstimateSettingsModal from './EstimateSettingsModal';
import { useCalculateTotals } from '@/utils/calculationUtils';
import { useGlassContext } from '@/context/GlassContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

// Helper function to calculate square footage
const calculateSqFt = (width: number, height: number): number => {
  return (width * height) / 144; // Convert square inches to square feet
};

const EstimateSummary: React.FC = () => {
  const { estimateItems, settings, taxRate, setTaxRate } = useEstimate();
  const { glassTypes } = useGlassContext();
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // Calculate totals using the useCalculateTotals hook
  const { subtotal, shipping, surcharge, tax, total, weight: totalWeight, area: totalSqFt } = useCalculateTotals(
    estimateItems,
    glassTypes,
    {
      shippingPercentage: settings.shippingRate,
      surchargePercentage: settings.surchargeRate,
      taxPercentage: settings.taxRate
    }
  );

  // Calculate other values
  const totalItems = estimateItems.length;
  let totalQuantity = 0;
  estimateItems.forEach(item => {
    totalQuantity += item.quantity;
  });

  // Get other values from settings
  const markup = subtotal * (settings.markupRate / 100);
  const otherFee = settings.otherFee;

  // Calculate the final total including markup and other fee
  const finalTotal = total + markup + otherFee;

  // Handle tax rate change
  const handleTaxRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || !isNaN(parseFloat(value))) {
      setTaxRate(value === '' ? 0 : parseFloat(value));
    }
  };

  return (
    <Card className="bg-[#1e293b] text-white border-[#4b5563]">
      <CardHeader>
        <div className="flex flex-row items-center gap-2">
          <CardTitle className="text-[#60a5fa]">Estimate Summary</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSettingsModalOpen(true)}
            className="h-6 w-6 p-0.5 text-[#60a5fa] hover:text-white hover:bg-[#243447]"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      {/* Settings Modal */}
      <EstimateSettingsModal
        open={settingsModalOpen}
        onOpenChange={setSettingsModalOpen}
      />
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Total Items</p>
            <p className="text-lg font-medium">{totalItems} types ({totalQuantity} pieces)</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Square Footage</p>
            <p className="text-lg font-medium">{totalSqFt.toFixed(2)} sq ft</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Weight</p>
            <p className="text-lg font-medium">{totalWeight.toFixed(2)} lbs</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Subtotal</p>
            <p className="text-lg font-medium">{formatCurrency(subtotal)}</p>
          </div>
        </div>

        <div className="pt-4 border-t border-[#4b5563]">
          <div className="space-y-3">
            {/* Subtotal */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">Subtotal</p>
              <p className="text-base font-medium">{formatCurrency(subtotal)}</p>
            </div>

            {/* Surcharge */}
            {settings.surchargeRate > 0 && (
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">Surcharge ({settings.surchargeRate}%)</p>
                <p className="text-base font-medium">{formatCurrency(surcharge)}</p>
              </div>
            )}

            {/* Markup */}
            {settings.markupRate > 0 && (
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">Markup ({settings.markupRate}%)</p>
                <p className="text-base font-medium">{formatCurrency(markup)}</p>
              </div>
            )}

            {/* Shipping */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">Shipping ({settings.shippingRate}%)</p>
              <p className="text-base font-medium">{formatCurrency(shipping)}</p>
            </div>

            {/* Tax */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">Tax ({settings.taxRate}%)</p>
              <p className="text-base font-medium">{formatCurrency(tax)}</p>
            </div>

            {/* Other Fee */}
            {settings.otherFee > 0 && (
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">Other Fee</p>
                <p className="text-base font-medium">{formatCurrency(otherFee)}</p>
              </div>
            )}
          </div>

          <div className="pt-4 mt-4 border-t border-[#4b5563]">
            <div className="flex justify-between items-center">
              <p className="text-lg font-medium">Total</p>
              <p className="text-xl font-bold text-[#60a5fa]">{formatCurrency(finalTotal)}</p>
            </div>
            {settings.roundUpGlassSize && (
              <div className="mt-2 text-xs text-muted-foreground text-right flex items-center justify-end gap-1">
                <span>* Prices calculated with dimensions rounded up to the next even inch</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-[#1e293b] text-white border-[#4b5563] max-w-xs">
                      <p>Selection rounds up all measurements to the next even number. This is typical practice from manufacturers providing quotes.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EstimateSummary;
